import { useSyncExternalStore } from "react";

export type BackendConnectionStatus =
  | "connected"
  | "connecting"
  | "disconnected";

export type BackendConnectionIssue = "none" | "reconnecting" | "final";

export type BackendConnectionState = Readonly<{
  status: BackendConnectionStatus;
  isConnected: boolean;
  /**
   * High-level UX state:
   * - none: everything OK (or initial startup)
   * - reconnecting: connection lost, retries in progress
   * - final: retries exhausted / user action required
   */
  issue: BackendConnectionIssue;
  /**
   * Increments whenever we transition into `connected`.
   * Useful to trigger refetch/resubscribe flows on reconnect.
   */
  connectionGeneration: number;
  lastDisconnectedAt: number | null;
  lastConnectedAt: number | null;
}>;

type Listener = () => void;

function computeIsConnected(status: BackendConnectionStatus): boolean {
  return status === "connected";
}

export class BackendConnectionStore {
  private listeners = new Set<Listener>();

  private state: BackendConnectionState = {
    status: "connecting",
    isConnected: false,
    issue: "none",
    connectionGeneration: 0,
    lastDisconnectedAt: null,
    lastConnectedAt: null,
  };

  public getSnapshot = (): BackendConnectionState => this.state;

  public subscribe = (listener: Listener): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  private emit(): void {
    for (const l of Array.from(this.listeners)) l();
  }

  public setStatus(nextStatus: BackendConnectionStatus): void {
    const prev = this.state;
    if (prev.status === nextStatus) return;

    const now = Date.now();
    const isConnected = computeIsConnected(nextStatus);

    // Bump generation only on transitions into "connected"
    const connectionGeneration =
      prev.status !== "connected" && nextStatus === "connected"
        ? prev.connectionGeneration + 1
        : prev.connectionGeneration;

    const lastDisconnectedAt =
      nextStatus === "disconnected" ? now : prev.lastDisconnectedAt;

    const issue: BackendConnectionIssue =
      nextStatus === "connected"
        ? "none"
        : prev.issue === "final"
          ? "final"
          : // Only show "reconnecting" if we've actually been disconnected before.
            // This prevents banners/toasts during initial startup.
            typeof lastDisconnectedAt === "number"
            ? "reconnecting"
            : "none";

    this.state = {
      status: nextStatus,
      isConnected,
      issue,
      connectionGeneration,
      lastDisconnectedAt,
      lastConnectedAt: nextStatus === "connected" ? now : prev.lastConnectedAt,
    };
    this.emit();
  }

  public setConnected(): void {
    this.setStatus("connected");
  }

  public setConnecting(): void {
    this.setStatus("connecting");
  }

  public setDisconnected(): void {
    this.setStatus("disconnected");
  }

  /**
   * Mark the connection as definitively lost (retries exhausted).
   */
  public setFinalDisconnected(): void {
    const prev = this.state;
    const now = Date.now();
    if (prev.status === "disconnected" && prev.issue === "final") return;
    this.state = {
      ...prev,
      status: "disconnected",
      isConnected: false,
      issue: "final",
      lastDisconnectedAt: prev.lastDisconnectedAt ?? now,
    };
    this.emit();
  }
}

export const backendConnectionStore = new BackendConnectionStore();

export function useBackendConnection(): BackendConnectionState {
  return useSyncExternalStore(
    backendConnectionStore.subscribe,
    backendConnectionStore.getSnapshot,
    backendConnectionStore.getSnapshot,
  );
}
